import { Box, Popper, TextField, styled } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

export const AutocompleteAdapter = ({
  input: { name },
  options,
  optionLabel,
  optionValue,
  initValue,
  setValue,
  submitObject,
  freeSolo,
  disabled,
  getMethodService,
  disablePortal,
  autoSelect, // BOOLEAN: changed in useEffect to set _value=null, autoComplete if option==1
  ...rest
}: any) => {
  const { input, meta } = useField(name);
  const optLabel = optionLabel ?? 'code';
  const optValue = optionValue ?? 'id';
  const safeOptions = Array.isArray(options) ? options : [];
  const useVirtualizedList = safeOptions.length > 100;

  const toInputLabel = (value: unknown) => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    return String((value as Record<string, unknown>)[optLabel] ?? '');
  };

  const onChange = (_e: SyntheticEvent, value: unknown) => {
    _setValue(value);
    setInputValue(toInputLabel(value));
    if (value) {
      if (submitObject === true) input.onChange(value);
      else
        input.onChange(
          typeof value === 'string' ? value : (value as Record<string, string>)[optValue] ?? ''
        );
    } else {
      input.onChange('');
    }
    if (setValue) {
      setValue(value);
    }
  };

  const handleDisabled = () => {
    if (freeSolo === true) return false;
    return safeOptions.length === 0;
  };

  const [_value, _setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  /** Tracks which init was last applied so we can skip re-apply after the user clears the field. */
  const lastAppliedInitKey = useRef('');

  /** Stable string so effects react when init content changes, not only object identity. */
  const initContentKey = useMemo(() => {
    if (initValue == null || initValue === '') {
      return '';
    }
    if (typeof initValue === 'object') {
      return `${String(initValue[optValue] ?? '')}::${String(initValue[optLabel] ?? '')}`;
    }
    return String(initValue);
  }, [initValue, optValue, optLabel]);

  useEffect(() => {
    if (!initValue) {
      if (safeOptions.length === 0 && _value !== null) {
        onChange(null, null);
        return;
      }
      if (safeOptions.length === 1 && autoSelect !== false) {
        // Auto-select the single available option if autoSelect is not explicitly false
        onChange(null, safeOptions[0]);
      } else if (
        safeOptions.length > 1 &&
        safeOptions.length < 20 &&
        _value !== null &&
        autoSelect !== false
      ) {
        // Use find method to search for a matching option based on the code property
        const matchedOption = safeOptions.find((option) => option.code === _value.code);

        if (matchedOption) {
          onChange(null, matchedOption);
        } else {
          onChange(null, null);
        }
      }
    } else {
      if (!safeOptions.length) {
        return;
      }
      if (lastAppliedInitKey.current === initContentKey && _value !== null) {
        return;
      }
      // L'utente ha svuotato il campo (FK opzionale): non riapplicare initValue dal parent
      if (input.value === '' && lastAppliedInitKey.current === initContentKey) {
        return;
      }
      const resolvedInit =
        typeof initValue === 'object' && initValue !== null
          ? safeOptions.find((o) => o[optValue] === initValue[optValue]) ?? initValue
          : safeOptions.find(
              (o) => o[optValue] === initValue || String(o[optValue]) === String(initValue)
            ) ?? initValue;
      onChange(null, resolvedInit);
      lastAppliedInitKey.current = initContentKey;
    }
  }, [options, initContentKey]);

  // Start strange stuff
  const LISTBOX_PADDING = 16; // px

  function renderRow(props: ListChildComponentProps) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
      lineHeight: '12px',
      paddingTop: '4px',
      fontSize: '12px',
      paddingBottom: '4px'
    };
    const { key, ...rowProps } = dataSet[0];

    return (
      <Box component="li" key={key} {...rowProps} style={inlineStyle}>
        {`${key}`}
      </Box>
    );
  }

  const OuterElementContext = React.createContext({});

  // eslint-disable-next-line react/display-name
  const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
  });

  function useResetCache(data: any) {
    const ref = React.useRef<VariableSizeList>(null);
    React.useEffect(() => {
      if (ref.current != null) {
        ref.current.resetAfterIndex(0, true);
      }
    }, [data]);
    return ref;
  }

  // Adapter for react-window
  const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
      const { children, ...other } = props;
      const itemData: React.ReactElement[] = [];
      (children as React.ReactElement[]).forEach(
        (item: React.ReactElement & { children?: React.ReactElement[] }) => {
          itemData.push(item);
          itemData.push(...(item.children || []));
        }
      );

      const theme = useTheme();
      const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true
      });
      const itemCount = itemData.length;
      const itemSize = smUp ? 36 : 48;

      const getChildSize = (child: React.ReactElement) => {
        // eslint-disable-next-line no-prototype-builtins
        if (child.hasOwnProperty('group')) {
          return 48;
        }

        return itemSize;
      };

      const getHeight = () => {
        if (itemCount > 8) {
          return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
      };

      const gridRef = useResetCache(itemCount);

      return (
        <div ref={ref}>
          <OuterElementContext.Provider value={other}>
            <VariableSizeList
              itemData={itemData}
              height={getHeight() + 2 * LISTBOX_PADDING}
              width="100%"
              ref={gridRef}
              outerElementType={OuterElementType}
              innerElementType="ul"
              itemSize={(index) => getChildSize(itemData[index])}
              overscanCount={5}
              itemCount={itemCount}>
              {renderRow}
            </VariableSizeList>
          </OuterElementContext.Provider>
        </div>
      );
    }
  );

  const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
      boxSizing: 'border-box',
      '& ul': {
        padding: 0,
        margin: 0
      }
    }
  });

  return (
    <Autocomplete
      disableClearable={false}
      freeSolo={freeSolo === true}
      disablePortal={disablePortal === true}
      {...(useVirtualizedList
        ? {
            disableListWrap: true,
            PopperComponent: StyledPopper,
            ListboxComponent: ListboxComponent,
            renderOption: (props, option, state) =>
              [props, option, state.index] as unknown as React.ReactNode
          }
        : {})}
      options={safeOptions}
      disabled={disabled ? true : handleDisabled()}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      getOptionLabel={(option) =>
        option == null ? '' : typeof option === 'string' ? option : option[optLabel] ?? ''
      }
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            {...rest}
            onKeyDown={(event) => {
              event.stopPropagation();
              rest.onKeyDown?.(event);
            }}
            helperText={meta.touched ? meta.error : ''}
            error={meta.error && meta.touched}
            fullWidth
          />
        );
      }}
      isOptionEqualToValue={(option, value) => {
        if (value == null || option == null) return false;
        if (typeof value === 'string') {
          return option[optValue] === value || option[optLabel] === value;
        }
        return option[optValue] === value[optValue];
      }}
      onChange={onChange}
      value={_value}
    />
  );
  // END strange stuff
};
